import { memo, useEffect, useImperativeHandle, useRef, type Ref } from 'react';

interface InnerHTMLDivProps extends React.HTMLAttributes<HTMLDivElement> {
  html: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Sets innerHTML via a ref + useEffect instead of dangerouslySetInnerHTML.
 *
 * React 19 re-applies dangerouslySetInnerHTML on every render, which
 * destroys and recreates the DOM subtree.  When the HTML contains resources
 * resolved by a service worker (e.g. images), each DOM reset triggers a
 * fresh load → layout shift → parent re-render → infinite loop.
 *
 * By assigning innerHTML imperatively only when the html string changes,
 * the existing DOM (and already-loaded images) is left untouched across
 * re-renders.
 */
const InnerHTMLDiv = memo(({ html, ref, ...props }: InnerHTMLDivProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => divRef.current as HTMLDivElement);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.innerHTML = html;
    }
  }, [html]);

  return <div ref={divRef} {...props} />;
});

InnerHTMLDiv.displayName = 'InnerHTMLDiv';

export default InnerHTMLDiv;
